import dayjs from 'dayjs';
import { isRequiredFor } from './required';
import { isComposite, isMeasurementValue } from './units';
import { isEmptyLocation } from './elementFormat/location';
import { decimalPlaces, stepOf } from './elementFormat/number';
import { isCurrencyValue } from './currency';
import { NUMBER_LIMITS, validateElementValue } from './elementValidation';
import { parseStoredDate, STORED_DATE_FORMAT } from './dateDisplay';
import { allowsMultipleDates } from './dateOptions';
import { elementDefaultValue } from './elementDefaults';
import { isConfirmationsElement, isConfirmationsType } from './confirmationValues';
import { isSeeOnlyElement } from './elementAccess';
import { choiceListOf } from './choiceValues';

const NUMBER_LIKE_TYPES = new Set(['number', 'decimal', 'integer', 'rating']);
const DATE_ONLY_TYPES = new Set(['date']);
const DATE_TIME_TYPES = new Set(['date-time']);
const TIME_ONLY_TYPES = new Set(['time']);

export function getElementInitialValue(element, { isNewRecord = false, now, zone } = {}) {
	const dataType = element?.dataType;
	const options = element?.options || {};
	const momentRef = now || dayjs();

	if (!dataType) return '';

	// A "Me too" count (KPR-390) has no default and nothing to type: a new
	// item holds none (absent reads as 0), and the form never sends one.
	if (isConfirmationsType(dataType)) return null;

	// A NEW item opens every element that has a default AT that default — the
	// value the API writes on every create path when the key is absent
	// (utils/elementDefaults.js; docs/plans/data-types-second-pass.md §3.6,
	// ruling D7). The date toggles win over a defaultValue; a default that no
	// longer fits the element's rules is not seeded, so the field starts as it
	// always did below, exactly as the server leaves the element blank. A
	// re-opened item never takes one: a default is where a new item starts.
	// Today, now and a relative date are read in `zone`, the COLLECTION's time
	// zone (the owner's ruling B11), as the server reads them — never the
	// browser's; without one, keepr's default (utils/elementDefaults).
	if (isNewRecord) {
		const seeded = elementDefaultValue(element, { now: momentRef, zone });
		if (seeded !== undefined) return seeded;
	}

	if (dataType === 'card-lookup') {
		return options?.allowMultiple ? [] : null;
	}

	// A user element's empty spellings are the contract's: '' for one
	// account, [] for several (keepr-api utils/userElements.js).
	if (dataType === 'user') {
		return options?.allowMultiple ? [] : '';
	}

	if (dataType === 'choice') {
		return null;
	}

	// A file element's empty spellings are the contract's: '' for one file,
	// [] for a list (PR 10b).
	if (dataType === 'file') {
		return options?.allowMultiple ? [] : '';
	}

	// A yes/no starts at No on a NEW item — a switch with no state is not a
	// thing anyone can look at — unless its default says Yes (above).
	// Re-opening a saved item whose value was never written leaves it absent
	// ("not set"), which is why this only fires for new records
	// (docs/plans/data-types.md decision 4).
	if (dataType === 'boolean') {
		return isNewRecord ? false : null;
	}

	// The three text-adjacent types all start as an empty STRING, including a
	// location: an untouched location field is '' on the wire (the frozen
	// contract's empty spelling), and the input assembles the
	// `{ address?, lat?, lng? }` object only once something is entered. A
	// null here would make "never filled in" and "cleared" two different
	// shapes for no gain.
	if (dataType === 'phone' || dataType === 'email' || dataType === 'location') {
		return '';
	}

	// A measurement's default — the { value, unit } the card author entered —
	// was seeded above when it still fits the element (B12: the API applies
	// the same one, held to the measure, units and min / max). Anything else
	// (a stale or legacy bare-number default, none at all) starts the field
	// empty rather than guessing a unit.
	if (dataType === 'measurement') return null;

	// Money with no default starts empty (the element's default CURRENCY is
	// what the entry control opens in, not a value). A default amount (ruling
	// D8) was seeded above, as the { amount, currency } the control holds.
	if (dataType === 'currency') return null;

	// A number's (and, since PR 4, a rating's) default was seeded above.
	if (NUMBER_LIKE_TYPES.has(dataType)) return null;

	// A date element that holds SEVERAL dates starts as a LIST — empty, or
	// holding the one date its default seeds (above, which wraps it). The
	// shape has to be right from the first render: an input that begins as a
	// scalar and becomes an array the first time it is touched is two
	// different fields.
	if (allowsMultipleDates({ dataType, options })) return [];

	// Each date type's default (above) is seeded in its own STORED shape
	// (utils/dateDisplay): a day for `date`, a clock time for `time`, an
	// instant for `date-time`, seconds included — a seeded "now" is a reading,
	// and two of them in the same minute must not collapse into one. A `date`
	// seeded as a timestamp is the bug the API's UTC-day slice turns into
	// yesterday for anyone east of UTC.
	if (DATE_ONLY_TYPES.has(dataType) || DATE_TIME_TYPES.has(dataType) || TIME_ONLY_TYPES.has(dataType)) {
		return null;
	}

	// Every other type — a short text included, whatever list it carries (an
	// inert one, kept from an earlier life as a Choice: KPE-033) — starts as
	// an empty string.
	return '';
}

export function isElementValueEmpty(element, value) {
	if (value === undefined || value === null) return true;
	// FALSE IS NOT EMPTY. "No" is an answer, and treating it as a blank would
	// make a yes/no unsaveable the moment anyone left it off.
	if (typeof value === 'boolean') return false;
	if (typeof value === 'string') return value.trim() === '';
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === 'number') {
		if (Number.isNaN(value)) return true;
		// antd Rate reports 0 for "no stars selected"
		if (element?.dataType === 'rating' && value === 0) return true;
		// A "Me too" count of 0 is nobody yet — the KQL reading of `is empty`
		// for the type (absent or 0), and what a tile hides (KPR-390).
		if (isConfirmationsType(element?.dataType) && value === 0) return true;
		return false;
	}
	// A measurement is an object with a finite magnitude; a partial object
	// (unit picked, nothing typed) is as empty as a blank input.
	if (element?.dataType === 'measurement' && typeof value === 'object') {
		return !isMeasurementValue(value);
	}
	// Money is an object with a whole-number `amount` (minor units) and a
	// code; anything less — a currency picked, nothing typed — is empty.
	if (element?.dataType === 'currency' && typeof value === 'object') {
		return !isCurrencyValue(value);
	}
	// A location is empty when it names neither a place nor a point — `{}`,
	// or an object whose address is blank and whose coordinates were never
	// given. HALF a coordinate is NOT empty: it is a mistake the validator
	// has to be allowed to report (the shared module draws that line).
	if (element?.dataType === 'location') {
		return isEmptyLocation(value);
	}
	return false;
}

/**
 * The two words a yes/no element shows for its states. A card author may
 * rename them ("Paid" / "Unpaid"); every surface that enters or renders a
 * boolean reads them from here, so one element never says Yes on the form and
 * true in the table. Blank or non-string options fall back to Yes / No.
 */
export function booleanLabels(element) {
	const options = element?.options || {};
	const trueLabel = typeof options.trueLabel === 'string' && options.trueLabel.trim() ? options.trueLabel.trim() : 'Yes';
	const falseLabel = typeof options.falseLabel === 'string' && options.falseLabel.trim() ? options.falseLabel.trim() : 'No';
	return { trueLabel, falseLabel };
}

/** A stored boolean as its element's label text; '' for anything not a boolean. */
export function formatBooleanValue(element, value) {
	if (typeof value !== 'boolean') return '';
	const { trueLabel, falseLabel } = booleanLabels(element);
	return value ? trueLabel : falseLabel;
}

// Step helpers for the "increment buttons" element option. Each returns the
// next stored value; they never mutate and treat blank/invalid input per spec.

// + / - lands on the next multiple of the element's step (PR 7, the `step`
// option; 1 when it has none): 2.5 -> 3 or 2 at step 1, 7 -> 7.5 or 6.5 at
// step 0.5, 12 -> 15 or 10 at step 5. A value already on the grid moves one
// whole step; blank counts as 0. The grid is anchored at 0, not at `min`
// (⚑ — antd's slider anchors at its min; the two agree whenever the min is a
// multiple of the step, which is the case a person sets up). The result is
// rounded to the step's own digits, so 0.1 + 0.2 lands on 0.3, not on
// 0.30000000000000004.
export function stepNumberValue(value, direction, step = 1) {
	const num = typeof value === 'number' ? value : parseFloat(value);
	const base = Number.isFinite(num) ? num : 0;
	const size = typeof step === 'number' && Number.isFinite(step) && step > 0 ? step : 1;
	const quotient = base / size;
	const nearest = Math.round(quotient);
	// Within float dust of a grid point is ON it (0.3 / 0.1 is 2.9999999999999996).
	const onGrid = Math.abs(quotient - nearest) < 1e-9 * Math.max(1, Math.abs(quotient));
	let index;
	if (direction > 0) index = onGrid ? nearest + 1 : Math.floor(quotient) + 1;
	else index = onGrid ? nearest - 1 : Math.ceil(quotient) - 1;
	return Number((index * size).toFixed(decimalPlaces(size)));
}

// Date chevrons move one day; the first click from a blank value sets today.
// In and out in the `date` storage shape ('YYYY-MM-DD'), though a legacy
// stored timestamp is still read — as its LOCAL day, the one it was picked on.
export function stepDateValue(value, direction, { now } = {}) {
	const current = parseStoredDate(value);
	if (!current) {
		return (now ? dayjs(now) : dayjs()).format(STORED_DATE_FORMAT);
	}
	return current.add(direction, 'day').format(STORED_DATE_FORMAT);
}

// Date-time +/- buttons move by `minutes` (1, 15, or 60). A value that isn't
// on the interval boundary first snaps to the boundary in the direction of
// travel (2:05 +15 -> 2:15, then +15 -> 2:30). Blank values start from now.
export function stepDateTimeValue(value, direction, minutes, { now } = {}) {
	const parsed = value ? dayjs(value) : null;
	const fallback = now ? dayjs(now) : dayjs();
	const base = (parsed && parsed.isValid() ? parsed : fallback).second(0).millisecond(0);
	if (minutes > 1) {
		const remainder = base.minute() % minutes;
		if (remainder !== 0) {
			return base.add(direction > 0 ? minutes - remainder : -remainder, 'minute').toISOString();
		}
	}
	return base.add(direction * minutes, 'minute').toISOString();
}

// Mobile keyboard hint for a numeric element. 'numeric' is the digits-only
// keypad; 'decimal' is the same pad plus the locale decimal separator, so it
// is the safe default for any type that isn't declared whole-number.
//
// Caveat worth knowing: neither iOS pad carries a minus key, so a negative
// value has to be pasted on iPhone. That is the price of the keypad, and it
// beats a full QWERTY keyboard for the overwhelmingly common positive case.
export function getNumericInputMode(dataType) {
	return dataType === 'integer' ? 'numeric' : 'decimal';
}

/**
 * The width class an entry-form field takes, sized to the data it holds
 * (platform form spec): numbers narrow, dates/ratings a fixed slot, long
 * text and composite measurements the whole row, everything else half.
 * Shared by the item form and the multi add page so a field looks the same
 * wherever it is asked.
 */
export function fieldWidthClass(element) {
	const type = element?.dataType;
	// A Choice's list only: a list another type carries is inert (KPE-033).
	const choices = choiceListOf(element);
	if (element?.options?.drivenFrom) return 'field--half';
	// A slider (PR 7) needs room to slide, with the number beside it.
	if (['number', 'decimal', 'integer'].includes(type)) return element?.options?.control === 'slider' ? 'field--half' : 'field--num';
	// A number plus a unit picker needs half a row; a composite (lb-oz: two
	// numbers plus the picker) takes the row so nothing wraps mid-run.
	if (type === 'measurement') return isComposite(element?.options?.defaultUnit) ? 'field--full' : 'field--half';
	// An amount plus its code (a suffix, or a currency picker): half a row,
	// as a single-unit measurement.
	if (type === 'currency') return 'field--half';
	// The 240px date slot fits a bare picker. Suffix step buttons ride the
	// same row as the picker (seven minute steps for a date-time, two day
	// chevrons for a date), and a date-time's "13 minutes ago" suffix sits
	// inside the picker beside the time — each needs more than the slot, or
	// the buttons wrap 6 + 1 and the suffix crowds the typed value.
	// A list of dates is a stack of date fields, each with its own remove,
	// under one label: half a row would leave the next field's label sitting
	// against a column of them.
	if (allowsMultipleDates(element)) return 'field--full';
	if (['date', 'date-time'].includes(type)) {
		const opts = element?.options || {};
		if (type === 'date-time' && opts.incrementButtons) return 'field--full';
		if (opts.incrementButtons || (type === 'date-time' && opts.showRelativeTime)) return 'field--half';
	}
	if (['date', 'date-time', 'time', 'rating'].includes(type)) return 'field--date';
	// A "Me too" count is a short read-only number (KPR-390): the number slot.
	if (isConfirmationsType(type)) return 'field--num';
	// A switch plus its label is a row of its own: half a row leaves the
	// label of the next field sitting against it.
	if (type === 'boolean') return 'field--full';
	// A location is an address input, a "use my location" button, and a
	// coordinates row that opens beneath them — more than half a row can hold
	// without the address squeezing to nothing. A phone and an email are
	// ordinary single inputs and fall through to half.
	if (type === 'location') return 'field--full';
	if (['text-large', 'rich-text'].includes(type)) return 'field--full';
	if (choices.length >= 2 && choices.length <= 5) return 'field--full';
	return 'field--half';
}

/**
 * The antd InputNumber constraints a number element's options come to:
 * `{ precision, min, max, step }` — the first three undefined when
 * unconstrained, `step` 1 unless the element sets one (PR 7). Shared by the
 * item form, the multi add page and the filter builder so the same field
 * accepts the same values wherever it is asked.
 *
 * Two rules the options alone do not say:
 *  - 'integer' IS decimals 0, whatever the option says (it is the legacy
 *    spelling of a whole-number element);
 *  - 'Disallow negatives' is a floor of ZERO, and only when no larger floor
 *    is already set — the switch may tighten a bound, never loosen one.
 */
export function numberInputConstraints(dataType, options) {
	const opts = options || {};
	const declared = Number.isInteger(opts.decimals) && opts.decimals >= 0 && opts.decimals <= NUMBER_LIMITS.MAX_DECIMALS
		? opts.decimals
		: undefined;
	const precision = dataType === 'integer' ? 0 : declared;
	const min = Number.isFinite(opts.min) ? opts.min : undefined;
	const max = Number.isFinite(opts.max) ? opts.max : undefined;
	const floor = opts.nonNegative && (min === undefined || min < 0) ? 0 : min;
	// The step the buttons, antd's arrows and the slider move by (PR 7): the
	// element's own when it fits the decimals the input keeps, else 1.
	const step = stepOf(opts, precision === undefined ? null : precision);
	return { precision, min: floor, max, step };
}

/**
 * The options the shared number formatter should read for an element of
 * `dataType` — which is the element's own options, unchanged.
 *
 * It looks like the place to fold in "'integer' IS decimals 0", and it used
 * to. It must not: the API's `decimalsOf` reads `options.decimals` and
 * nothing else, and the API's display path (utils/displayTitle.js) hands it
 * the raw options, so a web-side special case here would make a legacy 7.6
 * under an integer element read '8' on this screen and '7.6' in the title the
 * server stored. Whole-ness is enforced where it belongs — the WRITE path
 * rounds an integer element's value (utils/typedElements.js
 * numberConstraints), and the form's input pins precision 0 so nothing
 * fractional is typed in the first place.
 *
 * Kept as a named seam rather than inlined: the display call sites should go
 * on asking one question about a number's options, wherever the answer moves
 * next.
 */
export function numberFormatOptions(dataType, options) {
	return options || {};
}

/**
 * Is this element's value assigned by the SERVER rather than typed?
 *
 * Three reasons, one answer: `drivenFrom` (recomputed from related records),
 * `sequence` (a number allocated once, at create) and a `confirmations`
 * element (KPR-390: a count keepr keeps from the people who said "me too" —
 * typed by nobody, set only by each person's own confirm and take-back).
 * Every reader that has to decide "can a person put a value here" asks this
 * rather than testing the keys — testing keys one by one is how one of them
 * gets forgotten, which is exactly how a required sequence element would
 * have become an unfillable form field.
 */
// A manual order (KPR-1396, `options.rank`) is a fourth: keepr places the item
// when it is made and moved, and nobody types it.
export const isServerAssignedElement = def => !!(def?.options?.drivenFrom || def?.options?.sequence || def?.options?.rank === true || isConfirmationsElement(def));

/**
 * Can this person put a value here? Not when the server assigns it (above),
 * and not when an element rule holds them to "see only" (KPR-509,
 * utils/elementAccess) — the API keeps the stored value and refuses a change
 * (`element_read_only`, `reason: 'grant'`), so the form neither asks for it
 * as required nor judges a value they cannot alter. Kept apart from
 * isServerAssignedElement on purpose: an automation, which runs as the
 * collection, may still write a see-only element (AutomationRuleEditor reads
 * that one).
 */
export const isNotTheirsToChange = def => isServerAssignedElement(def) || isSeeOnlyElement(def);

/** Server-numbered specifically: the value is allocated at create. */
export const isSequenceElement = def => !!(def?.options?.sequence);

export function getMissingRequiredElements(elementDefs = [], values = {}) {
	return (elementDefs || []).filter(def => (
		def?.name && def?.options?.required && !isNotTheirsToChange(def)
		&& isElementValueEmpty(def, values?.[def.name])
	));
}

/**
 * Every reason a form cannot be saved, as `{ [elementName]: message }` in
 * DEFINITION ORDER (the caller scrolls to the first key, which is therefore
 * the topmost field). Two passes over the same list:
 *
 *   1. required — an empty value on an element that is required, flatly
 *      (`options.required`) or conditionally (`options.requiredWhen`, judged
 *      against the row as it stands);
 *   2. the value itself — utils/elementValidation, whose per-type rules
 *      mirror the API's write rules so a refusal reads the same either way.
 *
 * A required element that is empty is only reported once: there is nothing to
 * validate about a blank, and two messages under one field is one too many.
 * Driven elements are skipped — the platform fills them, the reader cannot.
 *
 * The whole list and the whole value set ride along as the validators'
 * context, because a date bound may name ANOTHER element ("no earlier than
 * Start") and can only be judged against the row as it stands.
 */
// `stored` (optional) is the item's element map as it is SAVED — the echo
// rule's input: a value identical to the stored one is never refused by the
// checks that keep it (the URL rule, the text ceilings, the rating scale;
// utils/elementValidation isEcho), as it is never refused by the API.
export function getElementValidationErrors(elementDefs = [], values = {}, { stored = null, attachmentsOff = false } = {}) {
	const errors = {};
	const context = { elements: elementDefs || [], values: values || {}, stored: stored || null };
	(elementDefs || []).forEach(def => {
		if (!def?.name || errors[def.name]) return;
		// A file element while the collection's attachments are off (PR 10b):
		// nothing can fill it — the server refuses a new file — so it is not
		// asked for, and a stored value saves unchanged (the echo).
		if (attachmentsOff && def.dataType === 'file') return;
		// Server-assigned either way: a driven value the engine computes, or a
		// number the server allocates on save. Neither can be typed, so
		// neither can be "required" of the reader and neither has a value for
		// the per-type rules to judge — a new item's number is blank until it
		// is saved, and a bound the author set would otherwise refuse the row
		// over a value nobody chose. The API skips both for the same reason
		// (utils/required/required.js). A see-only element is skipped too: the
		// person cannot change it, and the API's echo rule keeps it as stored.
		if (isNotTheirsToChange(def)) return;
		const value = values?.[def.name];
		// `required` OR `requiredWhen` — the same rule the API runs, from the
		// same vendored file (utils/required/required.js), so a field the form
		// lets through is never refused by the save and a field the form
		// demands is never one the API would have accepted.
		if (isRequiredFor(def, values || {}) && isElementValueEmpty(def, value)) {
			errors[def.name] = `${def?.label?.singular || def.name} is required.`;
			return;
		}
		const message = validateElementValue(def, value, context);
		if (message) errors[def.name] = message;
	});
	return errors;
}

export default getElementInitialValue;

/**
 * Groups elements into the panels the entry form and the read view share:
 * by the element's optional `options.group` (case-insensitive, trimmed), in
 * first-seen order, with ungrouped elements in a default panel wherever the
 * first of them appears. Returns [{ key, label, entries }], where each entry
 * is the caller's own record (pass `getElement` when entries wrap the element
 * — the form carries `{ element, def }` pairs; the read view passes elements
 * straight through).
 */
export function groupElementsIntoPanels(entries = [], { getElement = entry => entry } = {}) {
	const panels = [];
	const byKey = new Map();
	(entries || []).forEach(entry => {
		const element = getElement(entry);
		if (!element?.name) return;
		const group = (element?.options?.group || '').trim();
		const key = group.toLowerCase() || '__default__';
		let panel = byKey.get(key);
		if (!panel) {
			panel = { key, label: group || null, entries: [] };
			byKey.set(key, panel);
			panels.push(panel);
		}
		panel.entries.push(entry);
	});
	return panels;
}
